import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Plus, Heart, Clock, Flame, ChevronDown } from "lucide-react";
import api, { resolveImage, formatUSD } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function Home() {
  const { lang } = useI18n();
  const [universes, setUniverses] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [team, setTeam] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/universes"),
      api.get("/products", { params: { featured: true } }),
      api.get("/team"),
      api.get("/testimonials"),
      api.get("/content"),
    ]).then(([u, f, tm, ts, c]) => {
      setUniverses(u.data); setFeatured(f.data); setTeam(tm.data);
      setTestimonials(ts.data); setContent(c.data.content);
    });
  }, []);

  const cc = (en, fr, fallback = "") => content ? ((lang === "fr" ? content[fr] : content[en]) || fallback) : fallback;

  return (
    <div className="bg-white min-h-screen overflow-x-hidden text-[var(--dsf-text)]">
      <Header transparent />

      {/* ================= HERO (deep green) ================= */}
      <section className="relative dd-pattern-leaf rounded-b-[40px] overflow-hidden" data-testid="hero-section">
        <div className="px-6 md:px-12 lg:px-20 pt-24 md:pt-32 lg:pt-36 pb-8 md:pb-12 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-4 items-center max-w-7xl mx-auto">
            {/* Left text */}
            <Reveal direction="left" className="lg:col-span-5 order-1">
              <div>
                <h1 className="font-bold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-5 md:mb-8 max-w-[600px]">
                  {cc("hero_title", "hero_title_fr", lang === "fr" ? "De la cuisine de Dakar à votre porte" : "Delicious food at your doorstep")}
                </h1>
                <p className="text-white/75 text-base md:text-lg max-w-md mb-6 md:mb-10 leading-relaxed">
                  {cc("hero_subtitle", "hero_subtitle_fr", "")}
                </p>
                <Link to="/univers/street_food" className="btn-mustard" data-testid="hero-cta-primary">
                  {cc("hero_cta", "hero_cta_fr", lang === "fr" ? "Commencer" : "Get Started")}
                </Link>
              </div>
            </Reveal>

            {/* Right visual */}
            <Reveal direction="right" delay={150} className="lg:col-span-7 order-2">
              <div className="relative w-full h-auto lg:h-[820px] flex items-center justify-center">
                {content?.hero_image ? (
                  <img
                    src={resolveImage(content.hero_image)}
                    alt="Dakar Street Food team"
                    className="w-full h-auto lg:h-full max-h-[420px] sm:max-h-[520px] lg:max-h-none object-contain drop-shadow-2xl lg:scale-110 lg:origin-bottom"
                  />
                ) : (
                  <div
                    className="md:absolute md:inset-0 w-full aspect-square bg-gradient-to-br from-[var(--dd-mustard)] via-[var(--dd-coral)] to-[var(--dd-green)]"
                    style={{ clipPath: "circle(45% at 45% 50%)" }}
                  />
                )}
                {/* Customer review bubble — only when admin set a quote */}
                {cc("hero_review_quote_en", "hero_review_quote_fr", "") && (
                  <div className="absolute top-6 right-2 md:right-12 bg-white dd-radius p-5 shadow-2xl max-w-[240px] hidden md:block">
                    <div className="flex gap-0.5 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={11} fill="currentColor" className="text-[var(--dd-mustard)]" />))}
                    </div>
                    <p className="text-xs text-[var(--dsf-text)] italic leading-relaxed">"{cc("hero_review_quote_en", "hero_review_quote_fr", "")}"</p>
                  </div>
                )}
                {/* Customer face round — only when admin uploaded */}
                {content?.hero_review_image && (
                  <div className="absolute top-32 right-0 md:right-4 hidden lg:block">
                    <img src={resolveImage(content.hero_review_image)} alt="" className="w-28 h-28 rounded-full object-cover ring-4 ring-white" />
                  </div>
                )}
                {/* Avatars + rating — only when admin uploaded at least one */}
                {(content?.hero_avatar1 || content?.hero_avatar2 || content?.hero_avatar3) && (
                  <div className="absolute bottom-8 right-2 md:right-8 bg-white dd-radius px-4 py-3 shadow-2xl flex items-center gap-3">
                    <div className="flex avatar-stack">
                      {[content?.hero_avatar1, content?.hero_avatar2, content?.hero_avatar3].filter(Boolean).map((url, i) => (
                        <img key={i} src={resolveImage(url)} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-white" />
                      ))}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-[var(--dd-green)]">{cc("hero_stats_label_en", "hero_stats_label_fr", "")}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={9} fill="currentColor" className="text-[var(--dd-mustard)]" />))}
                        </div>
                        <span className="text-[10px] font-semibold text-[var(--dsf-text)]">{content?.hero_stats_rating || "4.8"}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-4 left-2 dd-star text-4xl rotate-12">✦</div>
              </div>
            </Reveal>
          </div>
        </div>
        <button className="hidden md:flex absolute bottom-8 right-8 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 items-center justify-center text-white transition-colors">
          <ChevronDown size={18} />
        </button>
      </section>

      {/* ================= PROMO BENTO (4 cards: Street Food / Market / Events / Food Truck) ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-white" data-testid="promo-section">
        <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-3 gap-5 max-w-7xl mx-auto md:h-[920px]">
          {/* Card 1 - Street Food (Restaurant address) */}
          <Reveal className="h-full">
            <Link to={content?.promo1_link || "/univers/street_food"} className="promo-card dd-bg-mustard relative overflow-hidden block h-full" data-testid="promo-card-street-food">
              <div className="grid grid-cols-2 gap-3 h-full items-center">
                <div className="flex flex-col h-full justify-center">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/90 mb-2">RESTAURANT</div>
                  <h3 className="font-bold text-white text-2xl md:text-3xl leading-[1.05] mb-3">DAKAR<br/>STREET FOOD</h3>
                  <div className="text-xs text-white/95 leading-relaxed space-y-1 mb-4">
                    <div className="font-semibold">2923 Walnut Bend Ln<br/>Houston, TX 77042</div>
                    <div className="opacity-90">📞 (281) 846-6420</div>
                    <div className="opacity-90">🕒 11am – 10pm</div>
                  </div>
                  <span className="inline-flex items-center gap-2 bg-white text-[var(--dd-mustard)] px-4 py-2 rounded-full text-xs font-semibold shadow-md self-start">
                    Order Now <Flame size={12} className="text-[var(--dd-mustard)]" />
                  </span>
                </div>
                <div className="relative h-full">
                  <img src={resolveImage(content?.promo1_image) || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&q=80"} alt="Dakar Street Food" className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
                </div>
              </div>
            </Link>
          </Reveal>

          {/* Card 2 - Market (Marinades + Safara) */}
          <Reveal delay={120} className="h-full md:row-start-2">
            <Link to={content?.promo2_link || "/univers/street_market"} className="promo-card dd-bg-green relative overflow-hidden block h-full" data-testid="promo-card-market">
              <div className="grid grid-cols-2 gap-3 h-full items-center">
                <div className="flex flex-col h-full justify-center">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--dd-mustard)] mb-2">MARINADES & SAFARA</div>
                  <h3 className="font-bold text-white text-2xl md:text-3xl leading-[1.05] mb-3">DAKAR<br/>MARKET</h3>
                  <div className="text-xs text-white/95 leading-relaxed space-y-1 mb-4">
                    <div className="font-semibold">Chicken Marinade</div>
                    <div className="font-semibold">Lamb Marinade</div>
                    <div className="font-semibold">Safara</div>
                  </div>
                  <span className="inline-flex items-center gap-2 bg-[var(--dd-mustard)] text-white px-4 py-2 rounded-full text-xs font-semibold shadow-md self-start">
                    Order Now <Flame size={12} />
                  </span>
                </div>
                <div className="relative h-full">
                  <img src={resolveImage(content?.promo2_image) || "https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?w=600&q=80"} alt="Dakar Market" className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
                </div>
              </div>
            </Link>
          </Reveal>

          {/* Card 4 - Food Truck (address + hours) → opens Google Maps */}
          <Reveal delay={180} className="h-full md:row-start-3">
            <a
              href="https://www.google.com/maps/search/?api=1&query=2601+Blodgett+St+Houston+TX+77004"
              target="_blank"
              rel="noopener noreferrer"
              className="promo-card dd-bg-coral text-white relative overflow-hidden block h-full"
              data-testid="promo-card-food-truck"
            >
              <div className="grid grid-cols-2 gap-3 h-full items-center">
                <div className="flex flex-col h-full justify-center">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/85 mb-2">FOOD TRUCK</div>
                  <h3 className="font-bold text-white text-2xl md:text-3xl leading-[1.05] mb-3">DAKAR<br/>FOOD TRUCK</h3>
                  <div className="text-[11px] text-white/95 leading-relaxed space-y-1 mb-4">
                    <div className="font-semibold">2601 Blodgett St<br/>Houston, TX 77004</div>
                    <div className="opacity-90">📞 (346) 778-8287</div>
                    <div className="opacity-90">🕒 Wed–Thu 12pm – 10pm</div>
                    <div className="opacity-90">🕒 Fri–Sat 12pm – 2am</div>
                    <div className="opacity-90">🕒 Sun 12pm – 10pm</div>
                  </div>
                  <span className="inline-flex items-center gap-2 bg-white text-[var(--dd-coral)] px-4 py-2 rounded-full text-xs font-semibold shadow-md self-start">
                    Find Us <Flame size={12} />
                  </span>
                </div>
                <div className="relative h-full">
                  <img src={resolveImage(content?.promo3_image) || "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&q=80"} alt="Dakar Food Truck" className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
                </div>
              </div>
            </a>
          </Reveal>

          {/* Card 3 - Dakar Events (right, full height across all 3 rows) */}
          <Reveal delay={240} className="h-full md:col-start-2 md:row-start-1 md:row-span-3">
            <Link to="/evenements" className="promo-card text-white relative overflow-hidden block h-full" data-testid="promo-card-events">
              {/* Background image — full card */}
              <img
                src={resolveImage(content?.promo4_image) || "https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&q=80"}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Gradient overlay for legibility */}
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(110,13,37,0.78) 0%, rgba(31,78,61,0.78) 100%)" }} />

              <div className="relative z-10 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--dd-mustard)] mb-4">CATERING · PICKUP ONLY</div>
              <h3 className="relative z-10 font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-5 max-w-[85%] drop-shadow-lg">
                Dakar<br/>
                <span className="italic text-[var(--dd-mustard)]">Events</span>
              </h3>
              <div className="relative z-10 w-16 h-px bg-[var(--dd-mustard)] mb-5" />
              <div className="relative z-10 text-base md:text-lg font-light text-white/95 max-w-[80%] leading-snug">
                Weddings · Corporate · Diaspora galas — crafted with the soul of Dakar.
              </div>

              <div className="absolute bottom-6 left-6 right-6 z-20 flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5">
                  <div className="bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-md inline-block w-fit">
                    Pickup only
                  </div>
                  <div className="bg-white text-[var(--dd-burgundy,#6E0D25)] px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-md inline-block w-fit">
                    Catering trays
                  </div>
                </div>
                <span className="ml-auto inline-flex items-center gap-2 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] px-5 py-2.5 rounded-full text-xs font-semibold shadow-md">
                  Order Catering <Flame size={14} />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ================= FEATURED PRODUCTS ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <Reveal>
              <h2 className="font-bold text-[var(--dd-green)] text-3xl md:text-5xl leading-tight">{lang === "fr" ? "Nos coups de cœur" : "Our Top Picks"}</h2>
            </Reveal>
            <Reveal delay={100}>
              <Link to="/univers/street_food" className="text-sm font-semibold text-[var(--dd-green)] hover:text-[var(--dd-mustard-dark)] transition-colors hidden md:flex items-center gap-2">
                {lang === "fr" ? "Tout voir" : "View all"} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {featured.slice(0, 4).map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <Link to={`/produit/${p.slug || p.id}`} className="card-light group block" data-testid={`featured-product-${i}`}>
                  <div className="relative aspect-square overflow-hidden bg-[var(--dd-bone)]">
                    <img src={resolveImage(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {p.old_price && (
                      <div className="discount-badge">-{Math.round(((p.old_price - p.price) / p.old_price) * 100)}%</div>
                    )}
                    <button onClick={(e) => { e.preventDefault(); }} className="absolute top-3 right-3 heart-btn">
                      <Heart size={14} />
                    </button>
                    <div className="absolute top-3 right-12 bg-white/95 px-2 py-1 rounded-full text-[10px] font-bold text-[var(--dsf-text)] flex items-center gap-1">
                      <Star size={9} fill="currentColor" className="text-[var(--dd-mustard)]" /> {p.rating?.toFixed(1) || "4.8"}
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-semibold text-[var(--dd-green)] text-base md:text-lg leading-tight mb-1 group-hover:text-[var(--dd-mustard-dark)] transition-colors line-clamp-1">{p.name}</h3>
                    <div className="text-xs text-[var(--dsf-text-muted)] mb-3">{p.category}</div>
                    <div className="flex items-center gap-2 text-[10px] text-[var(--dsf-text-muted)] mb-3">
                      <Clock size={11} /> 18 min · 450 Kcal
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[var(--dd-green)] text-xl">{formatUSD(p.price)}</div>
                      <button className="btn-add-round" data-testid={`add-cart-${p.id}`}><Plus size={16} /></button>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= EXPLORE BY CATEGORY ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 dd-bg-green-soft" data-testid="universes-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
            <Reveal className="lg:col-span-7">
              <h2 className="font-bold text-[var(--dd-green)] text-3xl md:text-5xl lg:text-6xl leading-tight">
                {lang === "fr" ? <>Explorez<br/>nos univers <span className="text-[var(--dd-mustard-dark)]">savoureux</span></> : <>Explore Delicious Cuisine<br/>by <span className="text-[var(--dd-mustard-dark)]">Category</span></>}
              </h2>
            </Reveal>
            <Reveal direction="right" delay={150} className="lg:col-span-5">
              <p className="text-[var(--dsf-text-secondary)] text-base leading-relaxed">
                {lang === "fr"
                  ? "Notre mission : connecter les amoureux de bonne cuisine à leurs saveurs préférées. Que vous cherchiez une bouchée rapide ou des aventures culinaires."
                  : "Our mission is to connect food lovers with their favorite cuisines. Whether you're craving a quick bite or culinary adventures."}
              </p>
            </Reveal>
          </div>

          {/* Big pizza visual + floating universe chips */}
          <div className="relative aspect-[16/10] md:aspect-[16/9] max-w-5xl mx-auto rounded-[40px] overflow-hidden bg-white">
            <img src="https://images.unsplash.com/photo-1593787614887-004b2ffb0057?w=1600&q=80" alt="" className="w-full h-full object-cover" />
            {/* Floating chips */}
            {universes.map((u, i) => {
              const positions = [
                "top-[8%] left-[8%]",
                "top-[18%] right-[6%]",
                "bottom-[15%] left-[14%]",
              ];
              return (
                <Link key={u.slug} to={`/univers/${u.slug}`} className={`absolute ${positions[i] || "top-[10%] left-[10%]"} card-light p-3 md:p-4 flex items-center gap-2 md:gap-3 dd-radius w-[180px] md:w-[200px]`} data-testid={`universe-card-${u.slug}`}>
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl overflow-hidden shrink-0 bg-[var(--dd-mustard)]">
                    <img src={resolveImage(u.image)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="font-bold text-[var(--dd-green)] text-sm leading-tight">{u.name}</div>
                    <div className="text-[10px] text-[var(--dsf-text-muted)]">{i === 0 ? "12+ items" : i === 1 ? "20+ items" : "Rental"}</div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/univers/street_food" className="btn-green-pill" data-testid="universes-cta">
              {lang === "fr" ? "Voir toutes les catégories" : "View All Categories"}
            </Link>
          </div>
        </div>
      </section>

      {/* ================= FLASH DEALS ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-white" data-testid="flash-deals-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-bold text-[var(--dd-green)] text-3xl md:text-5xl leading-tight text-center mb-10">{lang === "fr" ? "Offres flash : se termine bientôt !" : "Flash Deals: Ending Soon!"}</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featured.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 100}>
                <div className="card-light p-5">
                  <div className="flex gap-4">
                    <div className="relative w-32 h-32 shrink-0 bg-[var(--dd-bone)] rounded-2xl overflow-hidden">
                      <img src={resolveImage(p.image)} alt={p.name} className="w-full h-full object-cover" />
                      <div className="absolute top-2 left-2 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] px-1.5 py-0.5 rounded-full text-[9px] font-bold">-{i === 0 ? 75 : i === 1 ? 50 : 60}%</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <button className="float-right text-[var(--dsf-text-muted)] hover:text-[var(--dd-coral)]"><Heart size={14} /></button>
                      <h3 className="font-bold text-[var(--dd-green)] text-base leading-tight mb-1 line-clamp-2">{p.name}</h3>
                      <div className="flex gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, j) => (<Star key={j} size={10} fill="currentColor" className="text-[var(--dd-mustard)]" />))}
                      </div>
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="font-bold text-[var(--dd-green)] text-lg">{formatUSD(p.price)}</span>
                        {p.old_price && <span className="text-xs text-[var(--dsf-text-muted)] line-through">{formatUSD(p.old_price)}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center border border-[var(--dd-green-soft)] rounded-full">
                          <button className="w-7 h-7 flex items-center justify-center text-[var(--dd-green)]">−</button>
                          <span className="px-2 text-sm font-semibold">1</span>
                          <button className="w-7 h-7 flex items-center justify-center text-[var(--dd-green)]">+</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between">
                    <div className="flex gap-2 text-xs">
                      {["00", "23", "45"].map((v, j) => (
                        <div key={j} className="bg-[var(--dd-bone)] px-2 py-1 rounded font-bold text-[var(--dd-green)]">{v}</div>
                      ))}
                    </div>
                    <button className="bg-[var(--dd-green)] text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-[var(--dd-green-deep)]" data-testid={`add-cart-flash-${p.id}`}>{lang === "fr" ? "Ajouter" : "Add to cart"}</button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TEAM ================= */}
      {/* ================= TESTIMONIALS ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-bold text-[var(--dd-green)] text-3xl md:text-5xl leading-tight text-center mb-12">{lang === "fr" ? "La parole de nos clients" : "What our guests say"}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((tm, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="card-light p-6 h-full" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: tm.rating }).map((_, j) => (<Star key={j} size={12} fill="currentColor" className="text-[var(--dd-mustard)]" />))}
                  </div>
                  <p className="text-[var(--dsf-text-secondary)] text-sm leading-relaxed mb-5">"{tm.text || tm.quote || tm.quote_fr}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-black/5">
                    {tm.image && <img src={resolveImage(tm.image)} alt={tm.name} className="w-10 h-10 rounded-full object-cover" />}
                    <div>
                      <div className="font-bold text-[var(--dd-green)] text-sm">{tm.name}</div>
                      <div className="text-xs text-[var(--dsf-text-muted)]">{tm.city || tm.role || ""}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Submit your own testimonial */}
          <TestimonialForm lang={lang} />
        </div>
      </section>

      {/* ================= CATERING CTA ================= */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-white" data-testid="catering-cta">
        <div className="dd-pattern-leaf rounded-[40px] overflow-hidden p-10 md:p-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-7xl mx-auto">
          <Reveal direction="left">
            <div className="text-xs uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-3">{cc("catering_eyebrow_en", "catering_eyebrow_fr", lang === "fr" ? "Traiteur" : "Catering")}</div>
            <h2 className="font-bold text-white text-3xl md:text-5xl leading-tight mb-5">
              {lang === "fr" ? <>Vos événements,<br/><span className="text-[var(--dd-mustard)]">notre signature.</span></> : <>Your events,<br/><span className="text-[var(--dd-mustard)]">our signature.</span></>}
            </h2>
            <p className="text-white/75 mb-8 leading-relaxed max-w-md">{cc("catering_body_en", "catering_body_fr", "")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/traiteur" className="btn-mustard">{lang === "fr" ? "Demander un devis" : "Request a quote"} <ArrowRight size={14} /></Link>
              <a href={`tel:${(content?.phone || "+1 (718) 555-0142").replace(/[^+\d]/g, "")}`} className="btn-pill-outline !text-white !border-white/30 hover:!bg-white hover:!text-[var(--dd-green)]">
                {content?.phone || "+1 (718) 555-0142"}
              </a>
            </div>
          </Reveal>
          <Reveal direction="right" delay={150}>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {[
                { v: content?.catering_stat1_value || "200+", l: cc("catering_stat1_label_en", "catering_stat1_label_fr", "Events / year") },
                { v: content?.catering_stat2_value || "50K", l: cc("catering_stat2_label_en", "catering_stat2_label_fr", "Guests served") },
                { v: content?.catering_stat3_value || "98%", l: cc("catering_stat3_label_en", "catering_stat3_label_fr", "Satisfaction") },
              ].map((s, i) => (
                <div key={i} className="bg-white/8 backdrop-blur dd-radius p-5 text-center border border-white/10">
                  <div className="font-bold text-3xl md:text-5xl text-[var(--dd-mustard)] leading-none">{s.v}</div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/70 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ---------- Testimonial submission form ----------
function TestimonialForm({ lang }) {
  const [form, setForm] = useState({ name: "", role: "", quote: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 10) return;
    setSubmitting(true);
    try {
      const r = await api.post("/testimonials/submit", form);
      setSubmitted(true);
      toast.success(r.data?.message || (lang === "fr" ? "Merci ! Votre témoignage sera publié après validation." : "Thanks! Your review will appear after approval."));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-16 max-w-2xl mx-auto text-center p-8 rounded-3xl bg-[var(--dd-green-soft)] border-2 border-dashed border-[var(--dd-green)]" data-testid="testimonial-thank-you">
        <div className="text-4xl mb-2">🙏</div>
        <div className="font-bold text-[var(--dd-green)] text-xl">{lang === "fr" ? "Merci pour votre partage !" : "Thanks for sharing!"}</div>
        <p className="text-[var(--dsf-text-muted)] text-sm mt-2">
          {lang === "fr" ? "Votre témoignage sera publié après validation par notre équipe." : "Your review will appear here once our team has approved it."}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-16 max-w-2xl mx-auto" data-testid="testimonial-form-section">
      <div className="text-center mb-6">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] font-bold mb-2">
          {lang === "fr" ? "Vous aussi" : "You too"}
        </div>
        <h3 className="font-bold text-[var(--dd-green)] text-2xl md:text-3xl">
          {lang === "fr" ? "Partagez votre expérience" : "Share your experience"}
        </h3>
      </div>
      <form onSubmit={submit} className="card-light p-6 md:p-8 space-y-4" data-testid="testimonial-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={lang === "fr" ? "Votre nom" : "Your name"}
            className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm focus:border-[var(--dd-green)] outline-none"
            data-testid="testimonial-input-name"
          />
          <input
            type="text" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            placeholder={lang === "fr" ? "Ville ou profession (optionnel)" : "City or role (optional)"}
            className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm focus:border-[var(--dd-green)] outline-none"
            data-testid="testimonial-input-role"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--dsf-text-muted)] mr-2">{lang === "fr" ? "Note :" : "Rating:"}</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n} type="button"
              onClick={() => setForm({ ...form, rating: n })}
              className="transition-transform hover:scale-110"
              data-testid={`testimonial-star-${n}`}
            >
              <Star size={22} fill={n <= form.rating ? "currentColor" : "transparent"} className={n <= form.rating ? "text-[var(--dd-mustard)]" : "text-gray-300"} />
            </button>
          ))}
        </div>
        <textarea
          required minLength={10} maxLength={600} rows={4} value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          placeholder={lang === "fr" ? "Racontez-nous votre expérience…" : "Tell us about your experience…"}
          className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm focus:border-[var(--dd-green)] outline-none resize-none"
          data-testid="testimonial-input-quote"
        />
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div className="text-[11px] text-[var(--dsf-text-muted)]">
            {lang === "fr" ? "Votre témoignage sera relu avant publication." : "Your review will be reviewed before publication."}
          </div>
          <button
            type="submit" disabled={submitting || form.quote.trim().length < 10}
            className="btn-mustard disabled:opacity-50"
            data-testid="testimonial-submit-button"
          >
            {submitting ? (lang === "fr" ? "Envoi…" : "Sending…") : (lang === "fr" ? "Envoyer mon avis" : "Send my review")}
          </button>
        </div>
      </form>
    </div>
  );
}
